import React, {useCallback, useEffect, useState, ChangeEvent} from 'react';
import http from "../../config/http-common";
import {Spinner} from "react-bootstrap";


interface User {
    Id: string;
    UserId: string;
    Name_lastname: string;
    Phone: string;
    Email: string;
    Address: string;
    RegNr: string;
    Company: string;
    PvnNr: string;
    CompanyAddress: string;
    WWW: string;
}

interface Unit {
    UnitId: string;
    UnitName: string;
    Unit: string;
    UnitPrice: number;
    Quantity: number;
    Price: number;
}

interface Product {
    ProductId: string;
    ProductName: string;
    SKU: string;
    UnitList: Unit[];
}

interface Item {
    CategoryId: string;
    Products: Product[];
    TotalPrice: number;
    TotalDiscount: number;
}

interface Basket {
    ID: string;
    UserID: string;
    Items: Item[];
    Status: number;
    CreatedAt: Date;
    UpdatedAt: Date;
}

interface Orders {
    user: User | null;
    basket: Basket;
    totalPrice: number;
    totalDiscount: number;
}

const OrderStatus: Record<number, string> = {
    0: 'Process',
    1: 'Ordered',
    2: 'Delivered',
    3: 'Payment',
    4: 'Canceled',
}

const Pasutijumi: React.FC<{}> = props => {
    const [isLoading, setIsLoading] = useState(true);
    const token = localStorage.getItem('token');
    const [orders, setOrders] = useState<Orders[]>([]);
    const [processOrders, setProcessOrders] = useState<Orders[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<number | null>(null);

    // Pagination state for main orders
    const [ordersPage, setOrdersPage] = useState<number>(1);
    const [ordersPageSize, setOrdersPageSize] = useState<number>(10);

    // Pagination state for processing orders
    const [procPage, setProcPage] = useState<number>(1);
    const [procPageSize, setProcPageSize] = useState<number>(10);

    // Modal state for order details dialog
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedOrder, setSelectedOrder] = useState<Orders | null>(null);

    const openOrderDialog = (order: Orders, e?: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setSelectedOrder(order);
        setShowModal(true);
    };

    const closeOrderDialog = () => {
        setShowModal(false);
        setSelectedOrder(null);
    };

    const loadData = useCallback(() => {
        setIsLoading(true);
        http.get('/api/orders', {
            headers: {
                'Token': token
            }
        })
            .then(response => {
                const raw = (response.data && (response.data.order || response.data.orders)) || [];
                // Normalize shapes/types defensively
                const all: Orders[] = (raw as any[]).map((o: any) => ({
                    user: o.user ?? null,
                    basket: {
                        ...o.basket,
                        Status: Number(o?.basket?.Status ?? o?.basket?.status ?? 0),
                    },
                    totalPrice: Number(o.totalPrice ?? 0),
                    totalDiscount: Number(o.totalDiscount ?? 0),
                }));
                const nonProcess = all.filter((order: Orders) => Number(order.basket.Status) !== 0);
                const processing = all.filter((order: Orders) => Number(order.basket.Status) === 0);
                setOrders(nonProcess);
                setProcessOrders(processing);
                // Reset pages to first page on reload to avoid out-of-range
                setOrdersPage(1);
                setProcPage(1);
            })
            .catch(error => {
                console.log(error);
            }).finally(() => {
            setIsLoading(false);
        });
    }, [token]);

    useEffect(() => {
        loadData();
    }, [loadData]);


    const handleStatusChange = (event: ChangeEvent<HTMLSelectElement>) => {
        event.stopPropagation(); // Prevents outer events (such as collapsable close) to be triggered
        event.nativeEvent.stopImmediatePropagation(); // Prevents other events in the same element to be triggered
        setSelectedStatus(Number(event.target.value));
    };

    const handleSelectedStatus = (ID: string) => {


            if (selectedStatus !== null) {
                http.put('/api/orders/status', {
                    ID: ID,
                    Status: selectedStatus
                }, {
                    headers: {
                        'Token': token
                    }
                })
                    .then(response => {
                        const raw = (response.data && (response.data.order || response.data.orders)) || [];
                        const all: Orders[] = (raw as any[]).map((o: any) => ({
                            user: o.user ?? null,
                            basket: {
                                ...o.basket,
                                Status: Number(o?.basket?.Status ?? o?.basket?.status ?? 0),
                            },
                            totalPrice: Number(o.totalPrice ?? 0),
                            totalDiscount: Number(o.totalDiscount ?? 0),
                        }));
                        setOrders(all.filter(o => Number(o.basket.Status) !== 0));
                        setProcessOrders(all.filter(o => Number(o.basket.Status) === 0));
                    })
                    .catch(error => {
                        console.log(error);
                    });
                console.log(processOrders)
            }

    };

    return (
        <div>
        <div className="container mt-4">

            {isLoading && (
                <div style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 9999
                }}>
                    <Spinner animation="border"/>
                </div>
            )}

            <div className="row">
                <div className="col-md-3">
                    <h2 className="mb-0">Pasūtījumi</h2>
                    <button className="btn btn-sm btn-outline-secondary ms-2" title="Pārlādēt" onClick={loadData}>
                        <i className="bi bi-arrow-clockwise"></i>
                    </button>
                </div>
            </div>

            <div className="row mb-2">
                <div className="col-6 d-flex align-items-center">
                    <label className="me-2">Rādīt pa lapai:</label>
                    <select className="form-select w-auto" value={ordersPageSize} onChange={(e) => { setOrdersPageSize(Number(e.target.value)); setOrdersPage(1); }}>
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                    <span className="ms-3 small text-muted">Kopā: {orders.length}</span>
                </div>
                <div className="col-6 text-end">
                    <button className="btn btn-outline-secondary me-2" disabled={ordersPage <= 1} onClick={() => setOrdersPage(p => Math.max(1, p - 1))}>Iepriekšējā</button>
                    <span>Lapa {ordersPage} no {Math.max(1, Math.ceil(orders.length / ordersPageSize))}</span>
                    <button className="btn btn-outline-secondary ms-2" disabled={ordersPage >= Math.ceil(orders.length / ordersPageSize)} onClick={() => setOrdersPage(p => Math.min(Math.ceil(orders.length / ordersPageSize) || 1, p + 1))}>Nākamā</button>
                </div>
            </div>

            <div className="card-list">

                {orders && orders.length === 0 && (
                    <div className="alert alert-info"><i className="bi bi-info-circle me-2"></i>Nav neviena pasūtījuma.</div>
                )}
                {orders && orders.slice((ordersPage - 1) * ordersPageSize, (ordersPage - 1) * ordersPageSize + ordersPageSize).map((order, index) => (
                    <div className="card shadow-sm mb-2" key={order.basket.ID || index}>
                        <div className="card-body p-0">
                            <div role="button" className="p-3" onClick={(e) => openOrderDialog(order, e)}>
                                <div className="container">
                                    <div className="row">
                                        <div className="col-3">
                                            <b>{order.user?.Company || order.user?.Name_lastname}</b>
                                        </div>
                                        <div className="col-3">
                                            Statuss: <span className={`badge ms-1 ${order.basket.Status===0?'bg-warning text-dark':order.basket.Status===1?'bg-primary':order.basket.Status===2?'bg-success':order.basket.Status===3?'bg-info text-dark':'bg-danger'}`}>{OrderStatus[order.basket.Status]}</span>
                                        </div>
                                        <div className="col-3">
                                            <i className="bi bi-calendar3 me-1" aria-hidden="true"></i>
                                            <span className="text-muted">Datums:</span> <b>{new Date(order.basket.CreatedAt).toLocaleDateString()}</b>
                                        </div>
                                        <div className="col-3">
                                            {order.basket.Items &&
                                                <>
                                                    <span className="text-muted">Kopā:</span>
                                                    {order.totalDiscount !== 0 && (
                                                        <span className="text-danger ms-1"><del>{(order.totalPrice).toFixed(2)} € </del></span>
                                                    )}
                                                    <span className="ms-2 fw-bold">{(order.totalPrice - order.totalDiscount).toFixed(2)} €</span>
                                                </>
                                            }
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                ))}


            </div>


        </div>

        <div className="container mt-4">

        <div className="row">
            <div className="col-md-6">
                <h2>Pasūtījumi kurus Klients vel gatavo! </h2> <i role="button" className="bi bi-arrow-clockwise m-1" onClick={loadData}></i>
            </div>
        </div>

        <div className="row mb-2">
            <div className="col-6 d-flex align-items-center">
                <label className="me-2">Rādīt pa lapai:</label>
                <select className="form-select w-auto" value={procPageSize} onChange={(e) => { setProcPageSize(Number(e.target.value)); setProcPage(1); }}>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                </select>
                <span className="ms-3 small text-muted">Kopā: {processOrders.length}</span>
            </div>
            <div className="col-6 text-end">
                <button className="btn btn-outline-secondary me-2" disabled={procPage <= 1} onClick={() => setProcPage(p => Math.max(1, p - 1))}>Iepriekšējā</button>
                <span>Lapa {procPage} no {Math.max(1, Math.ceil(processOrders.length / procPageSize))}</span>
                <button className="btn btn-outline-secondary ms-2" disabled={procPage >= Math.ceil(processOrders.length / procPageSize)} onClick={() => setProcPage(p => Math.min(Math.ceil(processOrders.length / procPageSize) || 1, p + 1))}>Nākamā</button>
            </div>
        </div>

        <div className="card-list">
            {processOrders && processOrders.length === 0 && (
                <div className="alert alert-info"><i className="bi bi-info-circle me-2"></i>Nav pasūtījumu, kurus klients gatavo.</div>
            )}
            {processOrders && processOrders.slice((procPage - 1) * procPageSize, (procPage - 1) * procPageSize + procPageSize).map((order, index) => (
                <div className="card shadow-sm mb-2" key={order.basket.ID || index}>
                    <div className="card-body p-0">
                        <div role="button" className="p-3" onClick={(e) => openOrderDialog(order, e)}>
                            <div className="container">
                                <div className="row">
                                    <div className="col-3">
                                        <b>{order.user?.Company || order.user?.Name_lastname}</b>
                                    </div>
                                    <div className="col-3">
                                        Statuss: <span className={`badge ms-1 ${order.basket.Status===0?'bg-warning text-dark':order.basket.Status===1?'bg-primary':order.basket.Status===2?'bg-success':order.basket.Status===3?'bg-info text-dark':'bg-danger'}`}>{OrderStatus[order.basket.Status]}</span>
                                    </div>
                                    <div className="col-3">
                                        <i className="bi bi-calendar3 me-1" aria-hidden="true"></i>
                                        <span className="text-muted">Datums:</span> <b>{new Date(order.basket.CreatedAt).toLocaleDateString()}</b>
                                    </div>
                                    <div className="col-3">
                                        {order.basket.Items &&
                                            <>
                                                <span className="text-muted">Kopā:</span>
                                                {order.totalDiscount !== 0 && (
                                                    <span className="text-danger ms-1"><del>{(order.totalPrice).toFixed(2)} € </del></span>
                                                )}
                                                <span className="ms-2 fw-bold">{(order.totalPrice - order.totalDiscount).toFixed(2)} €</span>
                                            </>
                                        }
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            ))}


        </div>


    {/* Order Details Modal */}
    {showModal && selectedOrder && (
        <div>
            <div className="modal fade show" style={{ display: 'block' }} aria-modal="true" role="dialog">
                <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Pasūtījums — {selectedOrder.user?.Company || selectedOrder.user?.Name_lastname}
                                <span className="ms-3 text-muted small">Datums: <b>{new Date(selectedOrder.basket.CreatedAt).toLocaleDateString()}</b></span>
                                <span className="ms-3">Statuss: <span className={`badge ms-1 ${selectedOrder.basket.Status===0?'bg-warning text-dark':selectedOrder.basket.Status===1?'bg-primary':selectedOrder.basket.Status===2?'bg-success':selectedOrder.basket.Status===3?'bg-info text-dark':'bg-danger'}`}>{OrderStatus[selectedOrder.basket.Status]}</span></span>
                                {selectedOrder.basket.Items && (
                                    <span className="ms-3">
                                        <span className="text-muted">Kopā:</span>
                                        {selectedOrder.totalDiscount !== 0 && (
                                            <span className="text-danger ms-1"><del>{(selectedOrder.totalPrice).toFixed(2)} € </del></span>
                                        )}
                                        <span className="ms-2 fw-bold">{(selectedOrder.totalPrice - selectedOrder.totalDiscount).toFixed(2)} €</span>
                                    </span>
                                )}
                            </h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={closeOrderDialog}></button>
                        </div>
                        <div className="modal-body">
                            <section>
                                <div className="row">
                                    <div className="col-4">
                                        <b>{selectedOrder.user?.Company || selectedOrder.user?.Name_lastname}</b>
                                    </div>
                                    <div className="col-5">
                                        <i className="bi bi-envelope me-1" aria-hidden="true"></i><b>{selectedOrder.user?.Email}</b>
                                        <span className="ms-3"></span>
                                        <i className="bi bi-telephone me-1" aria-hidden="true"></i><b>{selectedOrder.user?.Phone}</b>
                                    </div>
                                    <div className="col-3">
                                        Reg.Nr: <b>{selectedOrder.user?.RegNr}</b>
                                    </div>
                                </div>
                                <hr/>
                            </section>
                            <section>
                                {selectedOrder.basket.Items && selectedOrder.basket.Items.map((item, itemIndex) => (
                                    <div key={itemIndex}>
                                        {item.Products.map((prod, pIdx) => (
                                            <div className="row" key={pIdx}>
                                                <div className="col-7"><b>{prod.ProductName}</b> <span className="text-muted">(SKU: {prod.SKU})</span></div>
                                                <div className="col-5">
                                                    {prod.UnitList.map((unit, uIdx) => (
                                                        <div className="row" key={uIdx}>
                                                            <div className="col">{unit.UnitName} {unit.Unit} - {(unit.UnitPrice).toFixed(2)} € | qt: <b>{unit.Quantity}</b> | Total: <b>{(unit.Price).toFixed(2)} €</b></div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <hr/>
                                            </div>
                                        ))}
                                    </div>
                                ))}

                                <hr/>
                                <div className="row">
                                    <div className="col-7">
                                        <div className="row">
                                            <div className="col-2 ms-1">
                                                <label>Statuss:</label>
                                            </div>
                                            <div className="col-5">
                                                <select
                                                    className="form-control"
                                                    id="StatusModal" name="StatusModal"
                                                    onChange={handleStatusChange}>
                                                    {Object.entries(OrderStatus).map(([key, value]) => (
                                                        <option key={key} value={key} selected={selectedOrder.basket.Status === Number(key)}>{value}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-4">
                                                <button className="btn btn-success btn-block"
                                                    onClick={() => handleSelectedStatus(selectedOrder.basket.ID)}>
                                                    Mainīt
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-5 text-center">
                                        {/* Optional action buttons */}
                                    </div>
                                </div>
                            </section>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={closeOrderDialog}>Aizvērt</button>
                        </div>
                    </div>
                </div>
            </div>
            {/* Backdrop */}
            <div className="modal-backdrop fade show" onClick={closeOrderDialog}></div>
        </div>
    )}

    </div>
        </div>
)
    ;
}

export default Pasutijumi;